import {
  ApiChartAttribute,
  ApiDimension,
  ApiMetric,
  ApiYAxisChartType,
} from "@incendium/api";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { removeFirstDimension, useChartData } from "features/analytics";
import { StatCardRound, StatCardRoundTypography } from "Components/UI/StatCard";
import { StyledComposedChart } from "features/analytics";
import { formatPercentage, percentageChange } from "Helpers/percentage";
import { useMemo } from "react";
import { truncate } from "Helpers/truncate";
import { useFromToContext } from "Providers/FromToProvider";
import { useSelectedProject } from "Hooks";
import Loading from "Components/Loading/Loading";
import IncreaseDecrease from "../components/IncreaseDecrease";
import GlassCard from "Components/GlassCard/GlassCard";
import { useMetricName } from "features/analytics/hooks/useMetricName";

interface IAudienceEffectiveCardProps {
  attributes: ApiChartAttribute[];
}

function AudienceEffectiveCard({ attributes }: IAudienceEffectiveCardProps) {
  const theme = useTheme();
  const metricToName = useMetricName();
  const { chartOutput, chartComparisonOutput } = useFromToContext();
  const { selectedProject } = useSelectedProject();
  const chart = useMemo(
    () => ({
      name: "",
      dimension: [ApiDimension.DIMENSION_LEAD_AUDIENCE],
      yAxisKeys: [
        {
          fields: [
            ApiMetric.METRIC_SESSION_COUNT_PER_LEAD,
            ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD,
          ],
        },
      ],
      attributes,
    }),
    [attributes]
  );

  const { chartData, dimensions, loading, comparisonChartData: compData } = useChartData(
    selectedProject,
    chart,
    chartOutput,
    {
      comparisonFrom: chartComparisonOutput?.from,
      comparisonTo: chartComparisonOutput?.to,
    }
  );

  const parsedChartData = useMemo(() => {
    if (!chartData?.data) {
      return [];
    }
    return removeFirstDimension(chartData?.data, dimensions);
  }, [chartData, dimensions]);

  const incDecChartData = useMemo(() => {
    if (!parsedChartData) {
      return [];
    }
    return parsedChartData.map((d) => {
      const found = (compData?.data || []).find((c) => c.name === d.name);
      if (!found) {
        return {
          name: d.name,
          [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]: 0,
          [ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]: 0,
        };
      }
      return {
        name: d.name,
        [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]: percentageChange(
          Number(found[ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]) || 0,
          Number(d[ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]) || 0
        ),
        [ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]: percentageChange(
          Number(found[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]) || 0,
          Number(d[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]) || 0
        ),
      };
    });
  }, [parsedChartData, compData]);

  const mostEffective = useMemo(() => {
    return [...parsedChartData].sort(
      (a, b) =>
        (Number(b[ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]) || 0) -
        (Number(a[ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]) || 0)
    )[0];
  }, [parsedChartData]);

  const biggestIncrease = useMemo(() => {
    return incDecChartData.sort(
      (a, b) =>
        (Number(b[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]) || 0) -
        (Number(a[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]) || 0)
    )[0];
  }, [incDecChartData]);

  const absIncDec = useMemo(() => {
    if (!incDecChartData.length) {
      return 0;
    }

    const s = Math.abs(
      Number(
        incDecChartData.sort(
          (a, b) =>
            Math.abs(Number(b[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]) || 0) -
            Math.abs(Number(a[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]) || 0)
        )[0][ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]
      ) || 0
    );
    const e = Math.abs(
      Number(
        incDecChartData.sort(
          (a, b) =>
            Math.abs(
              Number(b[ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]) || 0
            ) -
            Math.abs(
              Number(a[ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]) || 0
            )
        )[0][ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]
      ) || 0
    );

    return e > s ? e : s;
  }, [incDecChartData]);

  return (
    <GlassCard sx={{ justifyContent: "center", minHeight: 408 }}>
      {loading ? (
        <Loading />
      ) : (
        <Stack direction={"row"} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ height: 24 }}>
              <Typography
                variant="subtitle1"
                sx={{ lineHeight: 1.2, textWrap: "pretty" }}
              >
                Average Sessions vs. Effective Sessions Per User
              </Typography>
            </Box>

            <Box
              mt={3}
              sx={{
                height: 290,
                width: "100%",
              }}
            >
              <StyledComposedChart
                noReOrder
                data={parsedChartData}
                xAxisProps={{
                  tickFormatter: (v: string) => truncate(v, 18),
                }}
                metricsOrder={[
                  ApiMetric.METRIC_SESSION_COUNT_PER_LEAD,
                  ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD,
                ]}
                yAxisKeys={[
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_SESSION_COUNT_PER_LEAD,
                      ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD,
                    ],
                    chart: {
                      [ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]:
                        ApiYAxisChartType.BAR,
                      [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]:
                        ApiYAxisChartType.DOT,
                    },
                  },
                ]}
                fills={{
                  [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]:
                    theme.palette.analytics.green,
                }}
                noLegend
                showTooltip
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ height: 24 }}>
              <Typography
                variant="subtitle1"
                sx={{ lineHeight: 1.2, textWrap: "pretty" }}
              >
                Changes in the Selected Time Period
              </Typography>
            </Box>

            <Box
              mt={3}
              sx={{
                height: 290,
                width: "100%",
              }}
            >
              <StyledComposedChart
                noReOrder
                data={incDecChartData}
                metricsOrder={[
                  ApiMetric.METRIC_SESSION_COUNT_PER_LEAD,
                  ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD,
                ]}
                xAxisProps={{
                  tickFormatter: (v) => truncate(v, 18),
                }}
                yAxisProps={[
                  {
                    tickFormatter: (v) => formatPercentage(v),
                    domain: [-absIncDec * 1.2, absIncDec * 1.2],
                  },
                ]}
                yAxisKeys={[
                  {
                    key: "l",
                    fields: [
                      ApiMetric.METRIC_SESSION_COUNT_PER_LEAD,
                      ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD,
                    ],
                    chart: {
                      [ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]:
                        ApiYAxisChartType.BAR,
                      [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]:
                        ApiYAxisChartType.DOT,
                    },
                  },
                ]}
                fills={{
                  [ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT_PER_LEAD]:
                    theme.palette.analytics.green,
                }}
                noLegend
                showTooltip
                tooltipProps={{
                  noSort: true,
                  renderValueFn: (key, v) =>
                    `${metricToName(key)} : ${formatPercentage(v)}`,
                }}
                showReferenceLineKey={"l"}
              ></StyledComposedChart>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 243,
              maxWidth: 243,
              height: 368,
              position: "relative",
            }}
          >
            <StatCardRound
              size="medium"
              sx={{ position: "absolute", bottom: 0, left: 0 }}
              backgroundColor={1}
            >
              {biggestIncrease && (
                <Stack mt={1} spacing={0.5} alignItems="center">
                  <StatCardRoundTypography size="large" lines={2}>
                    {biggestIncrease?.name}
                  </StatCardRoundTypography>
                  <StatCardRoundTypography size="small">
                    had the biggest increase in avg. sessions this time period
                  </StatCardRoundTypography>
                  <IncreaseDecrease
                    direction={
                      Number(
                        biggestIncrease[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]
                      ) > 0
                        ? "up"
                        : "down"
                    }
                    value={Number(
                      biggestIncrease[ApiMetric.METRIC_SESSION_COUNT_PER_LEAD]
                    )}
                    fontSize={28}
                    fontWeight={600}
                  />
                </Stack>
              )}
            </StatCardRound>
            <StatCardRound sx={{ position: "absolute", top: 0, right: 0 }}>
              {mostEffective?.name && (
                <>
                  <StatCardRoundTypography size="small">
                    Looks like
                  </StatCardRoundTypography>
                  <StatCardRoundTypography size="large" lines={2}>
                    {mostEffective?.name}
                  </StatCardRoundTypography>
                  <StatCardRoundTypography size="small">
                    sessions are proving most effective
                  </StatCardRoundTypography>
                </>
              )}
            </StatCardRound>
          </Box>
        </Stack>
      )}
    </GlassCard>
  );
}

export default AudienceEffectiveCard;
